import { useEffect, useRef, useState } from 'react';

/**
 * Refetch the price (for tax) when the zip becomes a valid 5-digit value.
 *
 * The payment form's zip field is registered with react-hook-form but has no
 * onApply hook like the coupon input, so on its own the backend never receives
 * the zip and tax stays at the zip-less $0. This watches the zip value and
 * triggers a refetch once it is a complete 5-digit zip.
 *
 * - Debounced so completing "12345" fires a single request, not one per keystroke.
 * - Guarded by a ref so re-renders / re-entering the same zip don't re-fetch.
 * - `refetch` is held in a ref and kept out of the effect deps: the controller's
 *   getPrice is not memoized (new identity every render), so depending on it would
 *   reset the debounce on every render. The effect should react only to the zip.
 *
 * Returns `isDebouncePending`: true from when a new valid zip is entered until
 * the debounced refetch fires. Callers OR this with the network loading flag to
 * cover the whole window where the displayed tax/total are stale, so the UI
 * never flashes a wrong total between "zip valid" and "new price resolved".
 */
export function useZipPriceRefetch(
  zip: string | null | undefined,
  refetch: () => void,
  debounceMs = 400,
): { isDebouncePending: boolean } {
  const lastFetchedZipRef = useRef<string | null>(null);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const [isDebouncePending, setIsDebouncePending] = useState(false);

  useEffect(() => {
    const value = (zip ?? '').trim();
    if (!/^\d{5}$/.test(value)) return;
    if (value === lastFetchedZipRef.current) return;
    setIsDebouncePending(true);
    const timer = setTimeout(() => {
      lastFetchedZipRef.current = value;
      setIsDebouncePending(false);
      refetchRef.current();
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [zip, debounceMs]);

  return { isDebouncePending };
}
